import type { DirectionTemplate } from '@/types/domain'

/* The route template's own rules, as functions — shared by the Settings editor
 * (`DirectionFormSheet`) and the leg page's «Сохранить как шаблон» (S-10), the
 * two surfaces that create one. Both write the SAME table through the same
 * endpoint, so they must refuse the same drafts before the request: a leg page
 * that let a name through which Settings refuses would discover it as a 422
 * after the leg itself had been saved.
 *
 * `RouteEditor` keeps its own copy of the day rule on purpose (see its note):
 * a LEG's chain is another table with another validator. The leg drawer's chain
 * becomes a template only through this file, which is why it reads the rule
 * here.
 */

/** Why a point's day count is refused, as a translation KEY rather than a
 *  sentence — `t` is typed against the catalogue, and threading it through a
 *  helper would widen its signature and throw the compile-time check away. */
export type DayProblem = 'common.form.required' | 'settings.directions.errors.wholeDays'

/** The day count INTO a point after the first: required, whole days.
 *
 *  Required because the server refuses a later template point without one —
 *  that is the transition the auto-advance is measured over, and a missing
 *  number is not a duration. Whole days because `parseInt` would silently take
 *  "3.7" as 3. */
export function dayProblem(raw: string): DayProblem | undefined {
  const trimmed = raw.trim()
  if (trimmed === '') return 'common.form.required'
  if (!/^\d+$/.test(trimmed)) return 'settings.directions.errors.wholeDays'
  return undefined
}

/** A template point's name bound (`maxDirectionPointName` on the server). A
 *  LEG point takes 200, so a chain typed on a leg can be legal there and too
 *  long to keep as a template. */
export const MAX_DIRECTION_POINT_NAME = 120

/** A name as the uniqueness index compares it: `lower(btrim(name))` over every
 *  template, active and inactive (0053, `direction_templates_name_unique_ci`). */
export function foldDirectionName(name: string): string {
  return name.trim().toLowerCase()
}

/** Whether a template already holds this name — checked against the
 *  UNFILTERED list, because a retired template still owns its name and the
 *  server refuses the second one. */
export function directionNameTaken(
  templates: readonly DirectionTemplate[],
  name: string,
): boolean {
  const folded = foldDirectionName(name)
  return folded !== '' && templates.some((template) => foldDirectionName(template.name) === folded)
}
